import React, { useEffect, useMemo, useRef } from "react";
import { POCKET_FILL, ROULETTE_POCKETS } from "../../utils/roulettePockets";

function createArc(cx, cy, innerRadius, outerRadius, startAngle, endAngle) {
  const largeArcFlag = endAngle - startAngle <= Math.PI ? "0" : "1";
  const x1 = cx + outerRadius * Math.cos(startAngle);
  const y1 = cy + outerRadius * Math.sin(startAngle);
  const x2 = cx + outerRadius * Math.cos(endAngle);
  const y2 = cy + outerRadius * Math.sin(endAngle);
  const x3 = cx + innerRadius * Math.cos(endAngle);
  const y3 = cy + innerRadius * Math.sin(endAngle);
  const x4 = cx + innerRadius * Math.cos(startAngle);
  const y4 = cy + innerRadius * Math.sin(startAngle);
  return [
    `M ${x1} ${y1}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

const RouletteWheel = ({
  size = 300,
  accent = "#64B5F6",
  spinning = false,
  idleSpin = true,
  rotationDeg = null,
  winningNumber = null,
  resultPocket = null,
  settleMs = 900,
}) => {
  const rotorRef = useRef(null);
  const cx = size / 2;
  const cy = size / 2;
  const outerRadius = size / 2;
  const innerRadius = outerRadius * 0.6;
  const numPockets = ROULETTE_POCKETS.length;
  const anglePerPocket = (2 * Math.PI) / numPockets;
  const labelFontSize = Math.max(8, Math.min(11, size * 0.038));

  const winningIndex = useMemo(() => {
    if (winningNumber == null) return null;
    return ROULETTE_POCKETS.findIndex((p) => p.number === winningNumber);
  }, [winningNumber]);

  useEffect(() => {
    const el = rotorRef.current;
    if (!el) return;

    if (spinning) {
      el.style.animation = "bs-roulette-spin 0.85s linear infinite";
      el.style.transform = "";
      return;
    }

    el.style.animation = "none";
    if (rotationDeg != null) {
      const easing =
        settleMs >= 5000
          ? "cubic-bezier(0.12, 0.82, 0.15, 1)"
          : "cubic-bezier(0.12, 0.85, 0.18, 1)";
      el.style.transition = `transform ${settleMs}ms ${easing}`;
      el.style.transform = `rotate(${rotationDeg}deg)`;
    } else {
      el.style.transition = "none";
      el.style.transform = "rotate(0deg)";
    }
  }, [spinning, rotationDeg, settleMs]);

  const hubSize = (innerRadius - 5) * 2;
  const displayPocket = resultPocket || (winningNumber != null
    ? ROULETTE_POCKETS.find((p) => p.number === winningNumber)
    : null);

  return (
    <div className="bs-roulette-wrap" style={{ width: size, height: size }}>
      <div className="bs-roulette-pointer" style={{ borderBottomColor: accent }} aria-hidden />
      <div
        ref={rotorRef}
        className={`bs-roulette-rotor${
          idleSpin && !spinning && rotationDeg == null ? "" : " bs-roulette-rotor-static"
        }${spinning ? " bs-roulette-rotor-fast" : ""}`}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <defs>
            <radialGradient id="bs-roulette-hub-bg" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#1E2836" />
              <stop offset="100%" stopColor="#0A0F18" />
            </radialGradient>
          </defs>

          <circle cx={cx} cy={cy} r={outerRadius} fill="#0A0F18" stroke={accent} strokeWidth="2" strokeOpacity="0.55" />

          <g>
            {ROULETTE_POCKETS.map((pocket, index) => {
              const startAngle = index * anglePerPocket;
              const endAngle = (index + 1) * anglePerPocket;
              const midAngle = (startAngle + endAngle) / 2;
              const textRadius = (innerRadius + outerRadius - 2) / 2;
              const textX = cx + textRadius * Math.cos(midAngle);
              const textY = cy + textRadius * Math.sin(midAngle);
              const textDeg = (midAngle * 180) / Math.PI + 90;
              const textColor = pocket.color === "black" ? "#FFFFFF" : "#0A0F18";
              const isWinner = winningIndex === index;

              return (
                <g key={`${pocket.number}-${index}`}>
                  <path
                    d={createArc(cx, cy, innerRadius, outerRadius - 2, startAngle, endAngle)}
                    fill={POCKET_FILL[pocket.color] || pocket.color}
                    stroke={isWinner ? accent : "rgba(255,255,255,0.12)"}
                    strokeWidth={isWinner ? 2.5 : 0.75}
                  />
                  <text
                    x={textX}
                    y={textY}
                    fill={textColor}
                    fontSize={labelFontSize}
                    fontWeight="800"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${textDeg}, ${textX}, ${textY})`}
                  >
                    {pocket.number}
                  </text>
                </g>
              );
            })}

            <circle
              cx={cx}
              cy={cy}
              r={innerRadius - 5}
              fill="url(#bs-roulette-hub-bg)"
              stroke={accent}
              strokeWidth="1.5"
              strokeOpacity="0.35"
            />
            <circle
              cx={cx}
              cy={cy}
              r={innerRadius * 0.42}
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="1"
            />
          </g>
        </svg>
      </div>

      <div
        className="bs-roulette-hub"
        style={{
          width: hubSize,
          height: hubSize,
          borderColor: accent,
          boxShadow: `0 0 24px ${accent}33, inset 0 0 18px rgba(0,0,0,0.55)`,
        }}
        aria-hidden
      >
        <div className="bs-roulette-hub-ring" style={{ borderColor: `${accent}66` }} />
        <div className="bs-roulette-hub-content">
          {displayPocket ? (
            <>
              <span className="bs-roulette-hub-result-num">{displayPocket.number}</span>
              <span
                className="bs-roulette-hub-result-dot"
                style={{ backgroundColor: POCKET_FILL[displayPocket.color] || displayPocket.color }}
              />
            </>
          ) : (
            <>
              <span className="bs-roulette-hub-kicker">Bond</span>
              <span className="bs-roulette-hub-title" style={{ color: accent }}>
                Spin
              </span>
              <div className="bs-roulette-hub-cap" style={{ backgroundColor: accent }} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouletteWheel;
